/** Driver app — Moj profil: registered credentials (read-only). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, UserRound, Phone, Mail, KeyRound, Landmark, Car, Fingerprint, Copy } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

const Row = ({ icon: Icon, label, value, testId, mono, copyable }) => (
  <div className="flex items-center gap-3 px-4 py-3.5" data-testid={testId}>
    <span className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center border border-[#C5A059]/30 bg-[#C5A059]/10 text-[#C5A059]">
      <Icon style={{ width: 18, height: 18 }} />
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
      <p className={`text-sm text-white truncate ${mono ? "font-mono tracking-wide" : ""}`}>
        {value || <span className="text-zinc-600">—</span>}
      </p>
    </div>
    {copyable && value && (
      <button
        onClick={() => { navigator.clipboard?.writeText(value); toast.success(`${label} kopiran.`); }}
        className="p-2 rounded-full text-zinc-500 hover:text-[#C5A059] hover:bg-white/5 transition-colors"
        aria-label={`Kopiraj ${label}`}
        data-testid={`${testId}-copy`}
      >
        <Copy className="w-4 h-4" />
      </button>
    )}
  </div>
);

const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
    <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.3em] text-[#C5A059]/80">{title}</p>
    <div className="divide-y divide-white/5">{children}</div>
  </div>
);

export default function DriverProfile() {
  const [p, setP] = useState(null);

  useEffect(() => {
    driverApi.get("/driver/profile")
      .then(({ data }) => setP(data))
      .catch(() => toast.error("Učitavanje profila nije uspjelo."));
  }, []);

  const initials = (p?.name || "")
    .split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 pb-16">
      <div className="max-w-xl mx-auto px-4 pt-6" data-testid="driver-profile-page">
        <Link to="/driver/dashboard" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white transition mb-5"
              data-testid="profile-back-link">
          <ArrowLeft className="w-4 h-4" /> Natrag na početnu
        </Link>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 flex items-center justify-center text-[#C5A059] text-xl font-semibold">
            {initials || <UserRound className="w-7 h-7" />}
          </div>
          <div>
            <h1 className="text-xl font-light text-white" data-testid="profile-name">{p?.name || "…"}</h1>
            <span className="inline-block mt-1 text-[9px] uppercase tracking-[0.25em] px-2.5 py-0.5 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 text-[#C5A059]"
                  data-testid="profile-role-badge">
              {p?.driver_type === "subcontractor" ? "Podizvođač" : "Vozač"}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <Card title="Kontakt i prijava">
            <Row icon={Phone} label="Broj mobitela" value={p?.phone} testId="profile-phone" copyable />
            <Row icon={Mail} label="Email" value={p?.email} testId="profile-email" copyable />
            <div className="flex items-center gap-3 px-4 py-3.5" data-testid="profile-pin">
              <span className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center border border-[#C5A059]/30 bg-[#C5A059]/10 text-[#C5A059]">
                <KeyRound style={{ width: 18, height: 18 }} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">PIN za prijavu</p>
                <p className="text-sm text-white font-mono tracking-[0.4em]">••••</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">Iz sigurnosnih razloga PIN je skriven — za promjenu kontaktirajte ured.</p>
              </div>
            </div>
          </Card>

          <Card title="Službeni podaci">
            <Row icon={Fingerprint} label="OIB" value={p?.oib} testId="profile-oib" mono copyable />
            <Row icon={Landmark} label="IBAN" value={p?.iban} testId="profile-iban" mono copyable />
          </Card>

          <Card title="Vozilo">
            <Row icon={Car} label="Vozilo / tablice" value={p?.vehicle_label} testId="profile-vehicle" />
          </Card>
        </div>

        <p className="text-[11px] text-zinc-600 mt-5 text-center">
          Primijetili ste netočan podatak? Javite se uredu i ažurirat ćemo ga.
        </p>
      </div>
    </div>
  );
}
